import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase/client";
import { Database } from "@/lib/database.types";
import { signUpWithTransaction } from "@/lib/services/auth";
import { setTokens as storeTokens, clearTokens } from "@/lib/token-store";
import { useIdleDetection } from "@/hooks/useIdleDetection";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

type AuthContextType = {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  error: Error | null;
  sessionExpired: boolean;
  showSessionExpiredModal: boolean;
  setShowSessionExpiredModal: (show: boolean) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    metadata: { full_name: string }
  ) => Promise<User | null>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  subscriptions: string[];
  refreshProfile: () => Promise<void>;
  createTenant: (name: string, slug: string) => Promise<any>;
  // Idle detection methods
  resetIdleTimer: () => void;
  getTimeUntilIdle: () => number;
  isIdleDetectionEnabled: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export the context for use in the hook
export { AuthContext };

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const manualSignOutRef = useRef(false);

  // Handle session expiration (either from idle timeout or session expiry)
  const handleSessionExpiration = () => {
    // Session expiration triggered
    setSessionExpired(true);
    setShowSessionExpiredModal(true);

    // Save current URL path before signing out to allow redirecting back
    const currentPath = window.location.pathname + window.location.search;
    if (!currentPath.startsWith("/login") && !currentPath.startsWith("/signup")) {
      sessionStorage.setItem("post_session_expired_redirect", currentPath);
      localStorage.setItem("post_session_expired_redirect", currentPath);
    }

    // Sign out the user after showing the modal
    setTimeout(async () => {
      try {
        manualSignOutRef.current = true;
        await supabase.auth.signOut();
      } catch (error) {
        console.error("Error signing out after session expiration:", error);
      }
    }, 1000);
  };

  // Idle detection hook
  const { resetTimer: resetIdleTimer, getTimeUntilIdle } = useIdleDetection({
    idleTime: IDLE_TIMEOUT,
    onIdle: handleSessionExpiration,
    enabled: !!user, // Only enable when user is logged in
  });

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      // Persist tokens for API client usage
      if (session?.access_token) {
        storeTokens(session.access_token, session.refresh_token || null);
      }
      if (session?.user) {
        getProfile(session.user.id);
        getSubscriptions(session.user.id);
      }
      setLoading(false);
    });

    // Listen for changes on auth state (signed in, signed out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      // Keep tokens in sync on auth events
      if (session?.access_token) {
        storeTokens(session.access_token, session.refresh_token || null);
      }
      if (session?.user) {
        getProfile(session.user.id);
        getSubscriptions(session.user.id);
        setSessionExpired(false);
        setShowSessionExpiredModal(false);
      } else {
        setProfile(null);
        setSubscriptions([]);
        clearTokens();
      }
      setLoading(false);

      if (event === "TOKEN_REFRESHED") {
        setUser(session?.user ?? null);
        if (session?.access_token) {
          storeTokens(session.access_token, session.refresh_token || null);
        }
        setSessionExpired(false);
        setShowSessionExpiredModal(false);
      }
      // Only show modal if NOT manual sign out
      if (event === "SIGNED_OUT" && user) {
        if (!manualSignOutRef.current) {
          setSessionExpired(true);
          setShowSessionExpiredModal(true);

          // Save current URL path before signing out to allow redirecting back
          const currentPath = window.location.pathname + window.location.search;
          if (!currentPath.startsWith("/login") && !currentPath.startsWith("/signup")) {
            sessionStorage.setItem("post_session_expired_redirect", currentPath);
            localStorage.setItem("post_session_expired_redirect", currentPath);
          }
        }
        manualSignOutRef.current = false; // reset for next time
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const getProfile = async (userId: string) => {
    try {
      const { data, error, status } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error && status !== 406) {
        throw error;
      }

      if (data) {
        setProfile(data);
      } else if (status === 406) {
        console.warn("User profile not found (406).");
        setProfile(null);
      }
    } catch (error) {
      console.error("Error loading user profile:", error);
    }
  };

  const getSubscriptions = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("plan_id, status, is_trial, trial_ends_at")
        .eq("user_id", userId)
        .eq("status", "active");

      if (error) {
        throw error;
      }

      if (data) {
        // Map subscription plans to features
        const activeSubscriptions = data
          .map((sub) => {
            if (sub.is_trial && sub.trial_ends_at) {
              const trialEndsAt = new Date(sub.trial_ends_at);
              if (trialEndsAt > new Date()) {
                return sub.plan_id.toLowerCase();
              }
            } else if (!sub.is_trial) {
              return sub.plan_id.toLowerCase();
            }
            return null;
          })
          .filter(Boolean) as string[];

        setSubscriptions(activeSubscriptions);
      }
    } catch (error) {
      console.error("Error loading subscriptions:", error);
    }
  };

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      setUser(data.user);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata: { full_name: string }
    ): Promise<User | null> => {
      try {
        const { user, error } = await signUpWithTransaction({
          email,
          password,
          full_name: metadata.full_name,
          plan_id: "empresarial",
          is_trial: true,
        });

        if (error) {
          if (error.includes("Ya tienes una invitación activa")) {
            throw new Error(error);
          }
          throw new Error(error);
        }

        if (user) {
          setUser(user);
          await getProfile(user.id);
          await getSubscriptions(user.id);
          return user;
        }
        return null;
      } catch (error) {
        setError(error as Error);
        throw error;
      }
    },
    [getProfile, getSubscriptions]
  );

  const signOut = async () => {
    try {
      manualSignOutRef.current = true;
      sessionStorage.removeItem("post_session_expired_redirect");
      localStorage.removeItem("post_session_expired_redirect");
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user?.id);

      if (error) throw error;

      if (user) {
        await getProfile(user.id);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await getProfile(user.id);
    }
  };

  const createTenant = async (name: string, slug: string) => {
    try {
      const { data, error } = await supabase
        .from("tenants")
        .insert({
          name,
          slug,
          user_id: user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      if (user) {
        await updateProfile({ tenant_id: data.id } as any);
      }

      return data;
    } catch (error) {
      console.error("Error creating tenant:", error);
      throw error;
    }
  };

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      error,
      sessionExpired,
      showSessionExpiredModal,
      setShowSessionExpiredModal,
      signIn,
      signUp,
      signOut,
      updateProfile,
      subscriptions,
      refreshProfile,
      createTenant,
      // Idle detection methods
      resetIdleTimer,
      getTimeUntilIdle,
      isIdleDetectionEnabled: !!user,
    }),
    [
      user,
      profile,
      loading,
      error,
      sessionExpired,
      showSessionExpiredModal,
      signIn,
      signUp,
      signOut,
      updateProfile,
      subscriptions,
      refreshProfile,
      createTenant,
      resetIdleTimer,
      getTimeUntilIdle,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the AuthContext
export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
